import React, { useMemo, useState } from 'react';
import { Box, Divider, ListItemIcon, MenuItem, Menu as MuiMenu, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import { useSelector } from 'react-redux';
import { withLanguage } from '../common/Texts';
import useAuthAction from '../../hooks/useAuthAction';
import { topbarTokens as t } from './tokens';
import { buildInitials, buildDisplayName } from '../../utils/userDisplay';

export default function ProfileMenu({ language }) {
    const tr = withLanguage(language);
    const first_name = useSelector((state) => state.user.first_name);
    const last_name = useSelector((state) => state.user.last_name);
    const username = useSelector((state) => state.user.username);
    const loggedIn = useSelector((state) => state.user.loggedIn);
    const runAuthAction = useAuthAction();

    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const initials = useMemo(
        () => buildInitials(first_name, last_name, username),
        [first_name, last_name, username]
    );
    const displayName = useMemo(
        () => buildDisplayName(first_name, last_name, username),
        [first_name, last_name, username]
    );

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleAuthAction = () => {
        handleClose();
        runAuthAction();
    };

    return (
        <>
            <Box
                component="button"
                type="button"
                onClick={handleOpen}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={displayName ? `${displayName} – ${tr('user_menu')}` : tr('user_menu')}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 0.75,
                    py: 0.5,
                    border: 'none',
                    background: 'transparent',
                    borderRadius: `${t.radius.md}px`,
                    cursor: 'pointer',
                    fontFamily: t.font.sans,
                    color: t.color.fg,
                    transition: 'background-color .15s',
                    '&:hover': { backgroundColor: t.color.bgSub },
                    '&:focus-visible': {
                        outline: `2px solid ${t.color.primaryBorder}`,
                        outlineOffset: 2,
                    },
                }}
            >
                <Box sx={{
                    width: 28, height: 28,
                    borderRadius: '50%',
                    backgroundColor: t.color.primarySoft,
                    color: t.color.primary,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                }}>
                    {initials}
                </Box>
                <Box component="span" sx={{
                    display: { xs: 'none', md: 'inline' },
                    fontSize: 13.5,
                    fontWeight: 500,
                    maxWidth: 160,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}>
                    {displayName}
                </Box>
            </Box>

            <MuiMenu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 1,
                            minWidth: 200,
                            borderRadius: `${t.radius.md}px`,
                            border: `1px solid ${t.color.border}`,
                            boxShadow: t.shadow.lg,
                            fontFamily: t.font.sans,
                        },
                    },
                }}
            >
                {displayName && (
                    <Box sx={{ px: 2, py: 1 }}>
                        <Typography sx={{
                            fontFamily: t.font.sans,
                            fontSize: 13,
                            fontWeight: 600,
                            color: t.color.fg,
                        }}>
                            {displayName}
                        </Typography>
                        {username && username !== displayName && (
                            <Typography sx={{
                                fontFamily: t.font.sans,
                                fontSize: 11.5,
                                color: t.color.fgSubtle,
                            }}>
                                {username}
                            </Typography>
                        )}
                    </Box>
                )}
                {displayName && <Divider sx={{ borderColor: t.color.divider }}/>}
                <MenuItem onClick={handleAuthAction} sx={{
                    fontFamily: t.font.sans,
                    fontSize: 13.5,
                    color: loggedIn ? t.color.danger : t.color.fg,
                }}>
                    <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                        {loggedIn ? <LogoutIcon fontSize="small"/> : <LoginIcon fontSize="small"/>}
                    </ListItemIcon>
                    {loggedIn ? tr('logout') : tr('login')}
                </MenuItem>
            </MuiMenu>
        </>
    );
}
